import { Inject, Injectable, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ApiClient } from '@lance-x/api-client';
import type {
  ForgotPasswordInput,
  LoginInput,
  RegisterEmpresaInput,
  RegisterProfissionalInput,
  ResetPasswordInput,
  TokenResponse,
} from '@lance-x/shared';
import { API_CLIENT } from '../http/api-client-token';
import { TOKEN_STORE, type TokenStore } from './token-store';
import { AuthStore } from './auth.store';
import { PushService } from '../push/push.service';
import { ForegroundBannerService } from '../push/foreground-banner.service';

/**
 * Imperative orchestrator over apiClient.auth + AuthStore + TokenStore.
 * Components call these methods; they should NEVER touch apiClient.auth directly,
 * so we keep session side-effects (store updates, navigation) in one place.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  // Phase 15 (PUSH-01/PUSH-03) — push UX must NEVER break auth, hence every
  // call below is wrapped in try/catch.
  private readonly pushService = inject(PushService);
  private readonly foregroundBanner = inject(ForegroundBannerService);

  constructor(
    @Inject(API_CLIENT) private readonly api: ApiClient,
    @Inject(TOKEN_STORE) private readonly tokenStore: TokenStore,
  ) {}

  private readonly isNative = Capacitor.isNativePlatform();

  /**
   * Phase 18 (KYC-02) — admin gate signal. Reads `MeResponse.isAdmin`
   * (populated from `User.isAdmin` server-side; see /auth/me handler).
   *
   * The server-side `AdminGuard` is the ONLY authoritative check — this signal
   * exists solely so the mobile UI can hide `/admin/*` routes for non-admin
   * users and avoid showing 403 dead-ends. Treat as a UX hint, never as a
   * security boundary: a determined client can flip a local signal but cannot
   * fool the DB-backed guard.
   *
   * Returns false when the user is not yet loaded (e.g. during bootstrap)
   * to fail closed.
   */
  readonly isAdmin = computed(() => this.authStore.user()?.isAdmin === true);

  private async applySession(res: TokenResponse): Promise<void> {
    this.authStore.setAccessToken(res.accessToken);
    this.tokenStore.setAccessToken(res.accessToken);
    if (res.refreshToken) {
      await this.tokenStore.setRefreshToken(res.refreshToken);
    }
    // Load /me for full profile (role + empresaId/profissionalId + funcoes/razaoSocial).
    const me = await this.api.auth.me();
    this.authStore.setSession(res.accessToken, me);
  }

  private resolveTargetUrl(redirectUrl?: string | null): string {
    if (
      redirectUrl &&
      redirectUrl.startsWith('/') &&
      !redirectUrl.startsWith('//')
    ) {
      return redirectUrl;
    }
    return this.authStore.shellPath();
  }

  async loginAndRedirect(
    input: LoginInput,
    redirectUrl?: string | null,
  ): Promise<void> {
    const res = await this.api.auth.login(input);
    await this.applySession(res);
    // Phase 15 — push UX is LOGIN-only (Threat T-15-11 — never on signup).
    // try/catch outside the auth flow: any push glitch must not block redirect.
    try {
      this.pushService.attachDeepLinkListeners();
      this.pushService.attachForegroundHandler((payload) =>
        this.foregroundBanner.show(payload).catch(() => undefined),
      );
      void this.pushService.promptForPermissionAfterFirstLogin();
    } catch (err) {
      console.warn('[auth] push wiring failed (non-fatal)', err);
    }
    await this.router.navigateByUrl(this.resolveTargetUrl(redirectUrl));
  }

  async registerEmpresa(
    input: RegisterEmpresaInput,
    redirectUrl?: string | null,
  ): Promise<void> {
    const res = await this.api.auth.registerEmpresa(input);
    await this.applySession(res);
    // No push prompt on signup (T-15-11).
    await this.router.navigateByUrl(this.resolveTargetUrl(redirectUrl));
  }

  async registerProfissional(
    input: RegisterProfissionalInput,
    redirectUrl?: string | null,
  ): Promise<void> {
    const res = await this.api.auth.registerProfissional(input);
    await this.applySession(res);
    // No push prompt on signup (T-15-11).
    await this.router.navigateByUrl(this.resolveTargetUrl(redirectUrl));
  }

  /**
   * Phase 16 v1.3 — SOCIAL-03. Called from oauth-callback.page after parsing
   * the JWT out of the deep-link query string. Stores the token in both the
   * AuthStore (drives Authorization headers) and TokenStore (Capacitor only),
   * then attempts /me — on success the user is fully authenticated; on
   * failure (network/transient) we keep the token but leave `user` null so
   * the caller can still navigate into complete-cadastro for OAuth-only
   * users in `pendente_completar` state.
   */
  async setToken(accessToken: string, refreshToken?: string | null): Promise<void> {
    this.authStore.setAccessToken(accessToken);
    this.tokenStore.setAccessToken(accessToken);
    if (refreshToken) {
      await this.tokenStore.setRefreshToken(refreshToken);
    }
    try {
      const me = await this.api.auth.me();
      this.authStore.setSession(accessToken, me);
    } catch {
      // /me failure is non-fatal here: OAuth-only users still need to
      // reach /complete-cadastro and the token is enough to authorize
      // that POST. Keep accessToken; leave user null.
    }
  }

  /**
   * Phase 16 v1.3 — SOCIAL-03. Returns true when the current session has a
   * concrete role assigned (i.e. NOT `pendente_completar`). Used by router
   * guards to decide whether the user should be funneled to
   * `/complete-cadastro` or allowed into their normal shell.
   */
  isProfileComplete(): boolean {
    const user = this.authStore.user();
    return user !== null && user.role !== null;
  }

  /**
   * Best-effort refresh. Returns true if a new access token was obtained.
   * Single-flight deduplicated so concurrent 401s or bootstrap calls never
   * send the same refreshToken twice in parallel (which would trigger
   * server-side refresh token reuse detection).
   */
  private refreshInFlight: Promise<boolean> | null = null;

  async refresh(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const refreshToken = (await this.tokenStore.getRefreshToken()) ?? undefined;
      const res = await this.api.auth.refresh(refreshToken);
      this.authStore.setAccessToken(res.accessToken);
      this.tokenStore.setAccessToken(res.accessToken);
      if (res.refreshToken) {
        await this.tokenStore.setRefreshToken(res.refreshToken);
      }
      // Refresh response also carries `user`; update store only if currently null
      // (avoid stomping a richer /me result loaded during bootstrap/login).
      if (this.authStore.user() === null) {
        try {
          const me = await this.api.auth.me();
          this.authStore.setSession(res.accessToken, me);
        } catch {
          // ignore; /me failure here is non-fatal
        }
      }
      return true;
    } catch {
      await this.tokenStore.setRefreshToken(null);
      return false;
    }
  }

  async logout(): Promise<void> {
    // T-15-09: revoke the server-side push token row BEFORE clearing JWT —
    // otherwise we lose the credential required to authorize the DELETE.
    try {
      await this.pushService.revokeCurrentToken();
    } catch {
      // never block logout on push errors
    }
    try {
      await this.api.auth.logout();
    } catch {
      // ignore: server-side revoke is best-effort; we still clear client state
    }
    await this.tokenStore.clear();
    this.authStore.clear();
    await this.router.navigateByUrl('/welcome');
  }

  /**
   * Called once at app boot. Attempts a silent refresh so a returning PWA user
   * stays logged in via the existing HttpOnly cookie. On any failure we silently
   * stay on /welcome — no toast, no error log to the user.
   * If refresh succeeds and the user booted into an auth/landing route (/, /welcome, /login),
   * automatically routes to their shell path (/empresa or /profissional) or /complete-cadastro.
   */
  async bootstrap(): Promise<void> {
    const ok = await this.refresh();
    if (ok) {
      const currentUrl = this.router.url;
      if (
        currentUrl === '/' ||
        currentUrl === '/welcome' ||
        currentUrl === '/login' ||
        currentUrl.startsWith('/login?') ||
        currentUrl.startsWith('/welcome?')
      ) {
        const target = this.authStore.user()?.role
          ? this.authStore.shellPath()
          : '/complete-cadastro';
        await this.router.navigateByUrl(target);
      }
    } else {
      // Make sure cleared state matches reality.
      this.authStore.clear();
    }
  }

  forgotPassword(input: ForgotPasswordInput) {
    return this.api.auth.forgotPassword(input);
  }

  resetPassword(input: ResetPasswordInput) {
    return this.api.auth.resetPassword(input);
  }
}
